import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import { ArrowLeft, CalendarClock, Download, Upload, FileText, CheckCircle2, Lock, Loader2, Paperclip } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { API_BASE } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDue } from '../lib/activityTypes';

const API = `${API_BASE}/api`;

const BANNER = {
  open: 'border-green-500/30 bg-green-500/10 text-green-200',
  soon: 'border-amber-500/40 bg-amber-500/10 text-amber-200',
  overdue: 'border-red-500/40 bg-red-500/10 text-red-200',
  none: 'border-zinc-700 bg-zinc-900 text-zinc-300',
};

const countdown = (hours) => {
  if (hours == null) return '';
  if (hours < 0) return 'Submissions are closed';
  if (hours < 24) return `${Math.max(1, Math.round(hours))} hour${Math.round(hours) === 1 ? '' : 's'} left`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} left`;
};

const DeadlineBanner = ({ deadline }) => (
  <div className={`flex flex-wrap items-center gap-3 rounded-2xl border p-4 sm:p-5 mb-8 ${BANNER[deadline.state]}`} data-testid="assignment-deadline-banner">
    {deadline.overdue ? <Lock className="h-6 w-6 shrink-0" /> : <CalendarClock className="h-6 w-6 shrink-0" />}
    <div className="flex-1 min-w-[200px]">
      <p className="text-xs uppercase tracking-[0.15em] opacity-80">Deadline</p>
      <p className="text-lg font-semibold text-white" data-testid="assignment-due-date">{deadline.due_at ? formatDue(deadline.due_at) : 'No deadline set'}</p>
    </div>
    {deadline.due_at && <span className="text-sm font-medium px-3 py-1.5 rounded-full bg-black/25" data-testid="assignment-countdown">{countdown(deadline.hours_left)}</span>}
  </div>
);

const Section = ({ title, html, testid }) => (html && html.replace(/<[^>]+>/g, '').trim() ? (
  <section className="mb-8" data-testid={testid}>
    <h2 className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-3">{title}</h2>
    <div className="rich-content text-zinc-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
  </section>
) : null);

async function downloadBlob(url, token, fallback) {
  const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
  const m = (res.headers['content-disposition'] || '').match(/filename="?([^"]+)"?/);
  const href = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = href;
  a.download = m ? m[1] : fallback;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

function SubmissionPanel({ data, onUpload, uploading }) {
  const fileRef = useRef(null);
  const sub = data.submission;
  const closed = data.deadline.overdue;
  const graded = sub?.status === 'graded';
  return (
    <aside className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5 lg:sticky lg:top-24 space-y-4" data-testid="assignment-submission-panel">
      <h3 className="text-white font-semibold">Your submission</h3>
      {sub ? (
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-3 text-sm" data-testid="assignment-current-submission">
          <p className="flex items-center gap-2 text-zinc-200"><FileText className="h-4 w-4 text-teal-400" /><span className="truncate">{sub.filename}</span></p>
          <p className="text-xs text-zinc-500 mt-1">Submitted {new Date(sub.submitted_at).toLocaleString()}</p>
          <p className={`mt-2 inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${graded ? 'bg-teal-500/15 text-teal-300' : 'bg-amber-500/15 text-amber-300'}`} data-testid="assignment-submission-status">
            <CheckCircle2 className="h-3.5 w-3.5" />{graded ? `Graded · ${sub.grade_percentage}%` : 'Submitted · awaiting grade'}
          </p>
          {sub.feedback && <p className="text-xs text-zinc-400 mt-2">Feedback: {sub.feedback}</p>}
        </div>
      ) : <p className="text-sm text-zinc-500">Nothing submitted yet.</p>}
      {data.enrolled && !graded && (closed ? (
        <p className="text-sm text-red-300 flex items-center gap-2" data-testid="assignment-closed-msg"><Lock className="h-4 w-4" />The deadline has passed — uploads are closed.</p>
      ) : (
        <>
          <input ref={fileRef} type="file" accept=".docx,.pdf" className="hidden" data-testid="assignment-file-input"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onUpload(f); }} />
          <Button disabled={uploading} onClick={() => fileRef.current?.click()} className="w-full bg-teal-600 hover:bg-teal-500 text-white" data-testid="assignment-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {sub ? 'Replace submission' : 'Upload your answer'}
          </Button>
          <p className="text-xs text-zinc-500">Word (.docx) or PDF, up to 25 MB. You can replace it until the deadline.</p>
        </>
      ))}
    </aside>
  );
}

export default function AssignmentPage() {
  const { programmeId, activityId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const base = `${API}/programmes/${programmeId}/assignments/${activityId}`;
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(() => {
    if (!token) return;
    axios.get(base, { headers }).then((r) => setData(r.data)).catch((e) => {
      toast.error(e.response?.data?.detail || 'Assignment not available');
      navigate(`/programmes/${programmeId}`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, token]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!['.docx', '.pdf'].includes(ext)) return toast.error('Only Word (.docx) or PDF files can be uploaded');
    if (file.size > 25 * 1024 * 1024) return toast.error('File must be 25 MB or smaller');
    const form = new FormData();
    form.append('file', file);
    setUploading(true);
    try {
      await axios.post(`${base}/submit`, form, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      toast.success('Assignment submitted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
    return null;
  };

  if (!data) return <Layout><div className="max-w-5xl mx-auto px-4 py-16"><div className="h-64 rounded-2xl bg-zinc-900 animate-pulse" /></div></Layout>;
  const a = data.assignment || {};
  return (
    <Layout breadcrumbItems={[
      { label: 'Dashboard', href: '/dashboard', isRoot: true },
      { label: 'Programmes', href: '/programmes' },
      { label: data.programme_name || 'Programme', href: `/programmes/${programmeId}` },
      { label: data.title, isCurrent: true },
    ]}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="assignment-page">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/programmes/${programmeId}`)} className="text-zinc-400 hover:text-white mb-6 -ml-2" data-testid="assignment-back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />Back to programme
        </Button>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-400 mb-2">Assignment{data.period_title ? ` · ${data.period_title}` : ''}</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-6" data-testid="assignment-title">{data.title}</h1>
        <DeadlineBanner deadline={data.deadline} />
        <div className="grid lg:grid-cols-[1fr_320px] gap-8 items-start">
          <div>
            <div className="flex flex-wrap gap-2 mb-8">
              <Button variant="outline" onClick={() => downloadBlob(`${base}/script`, token, 'Assignment.docx').catch(() => toast.error('Download failed'))} className="border-zinc-700 text-zinc-200" data-testid="assignment-download-script-btn">
                <Download className="h-4 w-4 mr-2" />Download assignment (Word)
              </Button>
              {data.has_brief && (
                <Button variant="outline" onClick={() => downloadBlob(`${API}/programmes/${programmeId}/activities/${activityId}/download-document`, token, 'Brief').catch(() => toast.error('Download failed'))} className="border-zinc-700 text-zinc-200" data-testid="assignment-download-brief-btn">
                  <Paperclip className="h-4 w-4 mr-2" />Attached brief
                </Button>
              )}
            </div>
            <Section title="Instructions" html={a.instructions_html || (data.description ? `<p>${data.description}</p>` : '')} testid="assignment-instructions" />
            {(a.scenarios || []).map((s, i) => (
              <div key={i} className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5 mb-6" data-testid={`assignment-scenario-${i}`}>
                <p className="text-[11px] uppercase tracking-[0.15em] text-amber-400 mb-1">Scenario {i + 1}</p>
                <h3 className="text-white font-semibold mb-3">{s.title}</h3>
                <div className="rich-content text-zinc-300" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(s.body_html || '') }} />
              </div>
            ))}
            <Section title="What to submit" html={a.deliverables_html} testid="assignment-deliverables" />
          </div>
          <SubmissionPanel data={data} onUpload={upload} uploading={uploading} />
        </div>
      </div>
    </Layout>
  );
}
